import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { GroupWithCount } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputText,
    ValidatedText,
    InputNullableDateTime,
    DateComp,
    InputSelect,
    DataSourceLookup,
    InputNullableInt,
    ValidatedInt,
    InputInt,
    InputBool,
    DisplayBool,
    InputDateTime,
} from '@/components';
import { type DateType } from '@/utilities/dateutils';
import { useEventCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';
import { useValidator } from '@/composeables';

export class GroupWithCountGuiDef extends GuiDef<GroupWithCount> {
    constructor() {
        super('GroupWithCount');

        const EventCommon = storeToRefs(useEventCommonStore());
        const RacesSource = computed(() => {
            return EventCommon.RacesAsSelectLine.value;
        });

        const NameValidator = new ValidatorDef({ Min: 1, Max: 99999999, Required: true, Type: undefined });
        const Name = new FieldDef<GroupWithCount>();
        Name.Name = 'Name';
        Name.FieldName = 'Name';
        Name.DefinedFor = 'Both';
        Name.TranslationKey = 'groupwithcount.name';
        Name.Editable = true;
        Name.Filterable = true;
        Name.Sortable = true;
        Name.CssClasses = '';
        Name.value = (groupwithcount) => groupwithcount.Name;
        Name.setValue = (groupwithcount, value) => (groupwithcount.Name = value);
        Name.DetailEditor = markRaw(InputText);
        Name.DetailEditorProps = {};
        Name.CellEditor = markRaw(InputText);
        Name.CellEditorProps = {};
        Name.CellRenderer = markRaw(ValidatedText);
        Name.CellRendererProps = {};
        Name.Validator = new ValidatorDef({ Min: 1, Max: 99999999, Required: true, Type: undefined });
        Name.SortDirection = undefined;
        Name.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(NameValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(NameValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        Name.MinWidth = undefined;
        Name.MaxWidth = undefined;
        Name.Tooltip = null;
        this.Fields.push(Name);

        const StartTimeValidator = new ValidatorDef({ Min: null, Max: null, Required: null, Type: 'eventactivetime' });
        const StartTime = new FieldDef<GroupWithCount>();
        StartTime.Name = 'StartTime';
        StartTime.FieldName = 'StartTime';
        StartTime.DefinedFor = 'Detail';
        StartTime.TranslationKey = 'groupwithcount.starttime';
        StartTime.Editable = true;
        StartTime.Filterable = true;
        StartTime.Sortable = true;
        StartTime.CssClasses = '';
        StartTime.value = (groupwithcount) => groupwithcount.StartTime;
        StartTime.setValue = (groupwithcount, value) => (groupwithcount.StartTime = value);
        StartTime.DetailEditor = markRaw(InputNullableDateTime);
        StartTime.DetailEditorProps = { format: 'DATETIME' as DateType, prefillUsingEventDate: true };
        StartTime.CellEditor = markRaw(InputNullableDateTime);
        StartTime.CellEditorProps = { format: 'DATETIME' as DateType, prefillUsingEventDate: true };
        StartTime.CellRenderer = markRaw(DateComp);
        StartTime.CellRendererProps = { format: 'DATETIME' as DateType, prefillUsingEventDate: true };
        StartTime.Validator = new ValidatorDef({ Min: null, Max: null, Required: null, Type: 'eventactivetime' });
        StartTime.SortDirection = undefined;
        StartTime.cellClassRules = [];
        StartTime.MinWidth = 180;
        StartTime.MaxWidth = undefined;
        StartTime.Tooltip = null;
        this.Fields.push(StartTime);

        const RaceIDValidator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        const RaceID = new FieldDef<GroupWithCount>();
        RaceID.Name = 'RaceID';
        RaceID.FieldName = 'RaceID';
        RaceID.DefinedFor = 'Both';
        RaceID.TranslationKey = 'groupwithcount.raceid';
        RaceID.Editable = true;
        RaceID.Filterable = false;
        RaceID.Sortable = true;
        RaceID.CssClasses = '';
        RaceID.value = (groupwithcount) => groupwithcount.RaceID;
        RaceID.setValue = (groupwithcount, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            groupwithcount.RaceID = value;
        };
        RaceID.DetailEditor = markRaw(InputSelect);
        RaceID.DetailEditorProps = { datasource: { mapsource: RacesSource } };
        RaceID.CellEditor = markRaw(InputSelect);
        RaceID.CellEditorProps = { datasource: { mapsource: RacesSource } };
        // @ts-expect-error @ts-ignore
        RaceID.CellRenderer = markRaw(DataSourceLookup);
        RaceID.CellRendererProps = { datasource: { mapsource: RacesSource } };
        RaceID.ExportValueFormatter = (obj: GroupWithCount) => {
            return RacesSource.value.get(obj.RaceID)?.text;
        };
        RaceID.Validator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        RaceID.SortDirection = undefined;
        RaceID.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(RaceIDValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(RaceIDValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        RaceID.MinWidth = undefined;
        RaceID.MaxWidth = undefined;
        RaceID.Tooltip = null;
        this.Fields.push(RaceID);

        const BibFrom = new FieldDef<GroupWithCount>();
        BibFrom.Group = new GroupDef('group.bibfromto', '', false);
        BibFrom.Name = 'BibFrom';
        BibFrom.FieldName = 'BibFrom';
        BibFrom.DefinedFor = 'Both';
        BibFrom.TranslationKey = 'groupwithcount.bibfrom';
        BibFrom.Editable = true;
        BibFrom.Filterable = false;
        BibFrom.Sortable = true;
        BibFrom.CssClasses = '';
        BibFrom.value = (groupwithcount) => groupwithcount.BibFrom;
        BibFrom.setValue = (groupwithcount, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            groupwithcount.BibFrom = value;
        };
        BibFrom.DetailEditor = markRaw(InputNullableInt);
        BibFrom.DetailEditorProps = {};
        BibFrom.CellEditor = markRaw(InputNullableInt);
        BibFrom.CellEditorProps = {};
        BibFrom.CellRenderer = markRaw(ValidatedInt);
        BibFrom.CellRendererProps = {};
        BibFrom.Validator = null;
        BibFrom.SortDirection = undefined;
        BibFrom.cellClassRules = undefined;
        BibFrom.MinWidth = undefined;
        BibFrom.MaxWidth = undefined;
        BibFrom.Tooltip = null;
        this.Fields.push(BibFrom);

        const BibTo = new FieldDef<GroupWithCount>();
        BibTo.Group = new GroupDef('group.bibfromto', '', false);
        BibTo.Name = 'BibTo';
        BibTo.FieldName = 'BibTo';
        BibTo.DefinedFor = 'Both';
        BibTo.TranslationKey = 'groupwithcount.bibto';
        BibTo.Editable = true;
        BibTo.Filterable = false;
        BibTo.Sortable = true;
        BibTo.CssClasses = '';
        BibTo.value = (groupwithcount) => groupwithcount.BibTo;
        BibTo.setValue = (groupwithcount, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            groupwithcount.BibTo = value;
        };
        BibTo.DetailEditor = markRaw(InputNullableInt);
        BibTo.DetailEditorProps = {};
        BibTo.CellEditor = markRaw(InputNullableInt);
        BibTo.CellEditorProps = {};
        BibTo.CellRenderer = markRaw(ValidatedInt);
        BibTo.CellRendererProps = {};
        BibTo.Validator = null;
        BibTo.SortDirection = undefined;
        BibTo.cellClassRules = undefined;
        BibTo.MinWidth = undefined;
        BibTo.MaxWidth = undefined;
        BibTo.Tooltip = null;
        this.Fields.push(BibTo);

        const WaveStart = new FieldDef<GroupWithCount>();
        WaveStart.Name = 'WaveStart';
        WaveStart.FieldName = 'WaveStart';
        WaveStart.DefinedFor = 'Both';
        WaveStart.TranslationKey = 'groupwithcount.wavestart';
        WaveStart.Editable = true;
        WaveStart.Filterable = true;
        WaveStart.Sortable = true;
        WaveStart.CssClasses = '';
        WaveStart.value = (groupwithcount) => groupwithcount.WaveStart;
        WaveStart.setValue = (groupwithcount, value) => (groupwithcount.WaveStart = value);
        WaveStart.DetailEditor = markRaw(InputNullableDateTime);
        WaveStart.DetailEditorProps = { format: 'DATETIME' as DateType };
        WaveStart.CellEditor = markRaw(InputNullableDateTime);
        WaveStart.CellEditorProps = { format: 'DATETIME' as DateType };
        WaveStart.CellRenderer = markRaw(DateComp);
        WaveStart.CellRendererProps = { format: 'DATETIME' as DateType };
        WaveStart.Validator = null;
        WaveStart.SortDirection = undefined;
        WaveStart.cellClassRules = undefined;
        WaveStart.MinWidth = 180;
        WaveStart.MaxWidth = undefined;
        WaveStart.Tooltip = null;
        this.Fields.push(WaveStart);

        const Sort = new FieldDef<GroupWithCount>();
        Sort.Name = 'Sort';
        Sort.FieldName = 'Sort';
        Sort.DefinedFor = 'Both';
        Sort.TranslationKey = 'groupwithcount.sort';
        Sort.Editable = true;
        Sort.Filterable = false;
        Sort.Sortable = true;
        Sort.CssClasses = '';
        Sort.value = (groupwithcount) => groupwithcount.Sort;
        Sort.setValue = (groupwithcount, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            groupwithcount.Sort = value;
        };
        Sort.DetailEditor = markRaw(InputInt);
        Sort.DetailEditorProps = {};
        Sort.CellEditor = markRaw(InputInt);
        Sort.CellEditorProps = {};
        Sort.CellRenderer = markRaw(ValidatedInt);
        Sort.CellRendererProps = {};
        Sort.Validator = null;
        Sort.SortDirection = undefined;
        Sort.cellClassRules = undefined;
        Sort.MinWidth = undefined;
        Sort.MaxWidth = undefined;
        Sort.Tooltip = null;
        this.Fields.push(Sort);

        const NumberOfLaps = new FieldDef<GroupWithCount>();
        NumberOfLaps.Name = 'NumberOfLaps';
        NumberOfLaps.FieldName = 'NumberOfLaps';
        NumberOfLaps.DefinedFor = 'Both';
        NumberOfLaps.TranslationKey = 'groupwithcount.numberoflaps';
        NumberOfLaps.Editable = true;
        NumberOfLaps.Filterable = false;
        NumberOfLaps.Sortable = true;
        NumberOfLaps.CssClasses = '';
        NumberOfLaps.value = (groupwithcount) => groupwithcount.NumberOfLaps;
        NumberOfLaps.setValue = (groupwithcount, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            groupwithcount.NumberOfLaps = value;
        };
        NumberOfLaps.DetailEditor = markRaw(InputNullableInt);
        NumberOfLaps.DetailEditorProps = {};
        NumberOfLaps.CellEditor = markRaw(InputNullableInt);
        NumberOfLaps.CellEditorProps = {};
        NumberOfLaps.CellRenderer = markRaw(ValidatedInt);
        NumberOfLaps.CellRendererProps = {};
        NumberOfLaps.Validator = null;
        NumberOfLaps.SortDirection = undefined;
        NumberOfLaps.cellClassRules = undefined;
        NumberOfLaps.MinWidth = undefined;
        NumberOfLaps.MaxWidth = undefined;
        NumberOfLaps.Tooltip = null;
        this.Fields.push(NumberOfLaps);

        const GrossTime = new FieldDef<GroupWithCount>();
        GrossTime.Name = 'GrossTime';
        GrossTime.FieldName = 'GrossTime';
        GrossTime.DefinedFor = 'Both';
        GrossTime.TranslationKey = 'groupwithcount.grosstime';
        GrossTime.Editable = true;
        GrossTime.Filterable = false;
        GrossTime.Sortable = true;
        GrossTime.CssClasses = '';
        GrossTime.value = (groupwithcount) => groupwithcount.GrossTime;
        GrossTime.setValue = (groupwithcount, value) => (groupwithcount.GrossTime = value);
        GrossTime.DetailEditor = markRaw(InputBool);
        GrossTime.DetailEditorProps = { label: 'groupwithcount.grosstime' };
        GrossTime.CellEditor = markRaw(InputBool);
        GrossTime.CellEditorProps = {};
        GrossTime.CellRenderer = markRaw(DisplayBool);
        GrossTime.CellRendererProps = {};
        GrossTime.Validator = null;
        GrossTime.SortDirection = undefined;
        GrossTime.cellClassRules = undefined;
        GrossTime.MinWidth = undefined;
        GrossTime.MaxWidth = undefined;
        GrossTime.Tooltip = null;
        this.Fields.push(GrossTime);

        const TimeBeforeRoundCount = new FieldDef<GroupWithCount>();
        TimeBeforeRoundCount.Name = 'TimeBeforeRoundCount';
        TimeBeforeRoundCount.FieldName = 'TimeBeforeRoundCount';
        TimeBeforeRoundCount.DefinedFor = 'Both';
        TimeBeforeRoundCount.TranslationKey = 'groupwithcount.timebeforeroundcount';
        TimeBeforeRoundCount.Editable = true;
        TimeBeforeRoundCount.Filterable = true;
        TimeBeforeRoundCount.Sortable = true;
        TimeBeforeRoundCount.CssClasses = '';
        TimeBeforeRoundCount.value = (groupwithcount) => groupwithcount.TimeBeforeRoundCount;
        TimeBeforeRoundCount.setValue = (groupwithcount, value) => (groupwithcount.TimeBeforeRoundCount = value);
        TimeBeforeRoundCount.DetailEditor = markRaw(InputDateTime);
        TimeBeforeRoundCount.DetailEditorProps = { format: 'TIME' as DateType };
        TimeBeforeRoundCount.CellEditor = markRaw(InputDateTime);
        TimeBeforeRoundCount.CellEditorProps = { format: 'TIME' as DateType };
        TimeBeforeRoundCount.CellRenderer = markRaw(DateComp);
        TimeBeforeRoundCount.CellRendererProps = { format: 'TIME' as DateType };
        TimeBeforeRoundCount.Validator = null;
        TimeBeforeRoundCount.SortDirection = undefined;
        TimeBeforeRoundCount.cellClassRules = undefined;
        TimeBeforeRoundCount.MinWidth = 100;
        TimeBeforeRoundCount.MaxWidth = undefined;
        TimeBeforeRoundCount.Tooltip = 'groupwithcount.timebeforeroundcounthelp';
        this.Fields.push(TimeBeforeRoundCount);

        const HideFromLive = new FieldDef<GroupWithCount>();
        HideFromLive.Name = 'HideFromLive';
        HideFromLive.FieldName = 'HideFromLive';
        HideFromLive.DefinedFor = 'Both';
        HideFromLive.TranslationKey = 'groupwithcount.hidefromlive';
        HideFromLive.Editable = true;
        HideFromLive.Filterable = false;
        HideFromLive.Sortable = true;
        HideFromLive.CssClasses = '';
        HideFromLive.value = (groupwithcount) => groupwithcount.HideFromLive;
        HideFromLive.setValue = (groupwithcount, value) => (groupwithcount.HideFromLive = value);
        HideFromLive.DetailEditor = markRaw(InputBool);
        HideFromLive.DetailEditorProps = { label: 'groupwithcount.hidefromlive' };
        HideFromLive.CellEditor = markRaw(InputBool);
        HideFromLive.CellEditorProps = {};
        HideFromLive.CellRenderer = markRaw(DisplayBool);
        HideFromLive.CellRendererProps = {};
        HideFromLive.Validator = null;
        HideFromLive.SortDirection = undefined;
        HideFromLive.cellClassRules = undefined;
        HideFromLive.MinWidth = undefined;
        HideFromLive.MaxWidth = undefined;
        HideFromLive.Tooltip = null;
        this.Fields.push(HideFromLive);

        const MaxContestants = new FieldDef<GroupWithCount>();
        MaxContestants.Name = 'MaxContestants';
        MaxContestants.FieldName = 'MaxContestants';
        MaxContestants.DefinedFor = 'Both';
        MaxContestants.TranslationKey = 'groupwithcount.maxcontestants';
        MaxContestants.Editable = true;
        MaxContestants.Filterable = false;
        MaxContestants.Sortable = true;
        MaxContestants.CssClasses = '';
        MaxContestants.value = (groupwithcount) => groupwithcount.MaxContestants;
        MaxContestants.setValue = (groupwithcount, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            groupwithcount.MaxContestants = value;
        };
        MaxContestants.DetailEditor = markRaw(InputNullableInt);
        MaxContestants.DetailEditorProps = {};
        MaxContestants.CellEditor = markRaw(InputNullableInt);
        MaxContestants.CellEditorProps = {};
        MaxContestants.CellRenderer = markRaw(ValidatedInt);
        MaxContestants.CellRendererProps = {};
        MaxContestants.Validator = null;
        MaxContestants.SortDirection = undefined;
        MaxContestants.cellClassRules = undefined;
        MaxContestants.MinWidth = undefined;
        MaxContestants.MaxWidth = undefined;
        MaxContestants.Tooltip = null;
        this.Fields.push(MaxContestants);
    }

    public async LoadDependencies() {
        await useEventCommonStore().EnsureLoaded(['Races']);
    }
}
